/* The ids row (2b-1, publish-wiring vet found 1, ruled 10/10) · the reads. With an API (VITE_API_URL) the establishments, their
   platforms and networks, the gift types, the roues, the demandes and the campaigns are the server's (contracts 02 §2.1–2.2,
   03, 04 §4.2–4.3, 05 §5.4): ServerMirror reads them once at the app's root into lib/mirror, where the getters find them, so
   every id a screen sends is one the server gave. The server's shapes become the app's here, field by field, and nowhere
   else. The drafts are written there too (saveDraft). 2b-2: the workspace's settings are read with them (02 §2.3), and
   lib/writes sends what the editors change. The members row: Équipe's people are read with them (09 §9.1). The demo
   (no VITE_API_URL) calls none of it: ServerMirror renders its routes at once and every getter reads the world's stores,
   as before. */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Button, Skeleton } from '@/ui'
import { ApiError, apiWrites, call, get } from './api'
import { isAuthPath, readMe, signInPath } from './auth'
import { mirror, queueWrite, setMirror, type Mirror, type ServerLocation } from './mirror'
import {
  createCampaign, documentOf, isServerId, readCampaign, refusalLines, savePending,
  type CampaignDocument, type DocStep, type CampaignRead, type CampaignRow, type DocDemande,
} from './campaign-api'
import { dropPending, getCampaign, recipeFromShape, rekeyCampaign, saveCampaign, type Campaign, type Demande, type DestinationRef, type Fiche, type ParcoursStep, type PlatformId, type Shape, type SocialId, type SocialRef } from './campaign'
import { DEFAULT_DESIGN, type GiftCategory, type GiftType, type RevealMoment, type Roue, type WheelDesign } from './wheel'
import type { Ask, AskKind, LinkAsk, QuizQuestion } from './asks'
import { IDENTITY_DEFAULT, SECTEURS, type IdentityFields, type SecteurId } from './scope'
import type { ServerWorkspace } from './mirror'
import { itemTagIds, type Tag } from './tags'
import { campaignCategoryId, organisationReady, type Category, type Place } from './organisation'
import type { Member } from './team'

/* ---------- the server's shapes (lib/writes sends and reads the same) ---------- */
export interface AppPlatform { locationId: string; platform: PlatformId; state: 'validee' | 'a-lier'; label: string; url: string | null; name: string | null; icon: string | null }
export interface AppSocial { locationId: string; social: SocialId; url: string; handle?: string | null; followers?: number | null }
export interface AppFiche { url: string | null; placeId: string | null; name: string | null; address: string | null; rating: number | null; hours: string[] | null; readAt: string | null
  /** lib-pack-22 · 02 §2.2 « Fiche Google »: the listing as linked (absent from an API before 20/10's google-fields) */
  ficheUrl?: string | null }
export interface AppGiftType {
  id: string; name: string; internal: string | null; costCents: number; validityDays: number | null; condition: string | null; icon: string | null
  category: GiftCategory; sameDay: boolean; links: string[]; archived: boolean
  stock: { capThisMonth: number; issuedThisMonth: number; status: 'ok' | 'low' | 'out' } | null
}
export interface AppRoue {
  id: string; name: string; scope: 'location' | 'group'; locations: string[]
  rewards: { rewardId: string; name: string; costCents: number; weight: number; cap: number | null; excludedReason: string | null }[]
  reveal: { moment: RevealMoment; countdown: 0 | 15 | 30 | 45; earlyOpen: boolean }
  texts: Record<string, string> | null; design: WheelDesign | null; usedBy: { campaignId: string; name: string }[]; pending: boolean
}
export interface AppAsk {
  id: string; kind: AskKind; name: string; category: string
  questions?: QuizQuestion[]; question?: string; answerType?: 'choix' | 'libre'; choices?: string[]; freeLine?: boolean; prompt?: string
  endMessage: string | null; createdAt: string; answers: number; usedBy: { campaignId: string; name: string }[]
}

/** 2b-2 · GET /v1/app/workspace (02 §2.3): the fields the app reads from it */
export interface AppWorkspace {
  sector: string | null; identityFields: Partial<IdentityFields> | null
  brand: { logoUrl: string | null; wheelDesign: Omit<WheelDesign, 'logoUrl'> | null; colour?: string | null } | null
}

/** The members row · GET /v1/app/members (09 §9.1): `numbers` is null on another member's row for a staff login. */
export interface AppMember {
  id: string; firstName: string; locationId: string | null; pinSet: boolean; access: Member['access']
  userId: string | null; personalSupportId: string | null
  numbers: { scans30d: number; participations30d: number; giftsValidated30d: number } | null
}

/* ---------- field by field (the adapter spec, ids-row-adapters §1–7) ---------- */
export const locationOf = (l: ServerLocation): ServerLocation => ({
  id: l.id, name: l.name, status: l.status, rating: l.rating ?? null,
  placeId: l.placeId ?? null, reviewUrl: l.reviewUrl ?? null, bookingUrl: l.bookingUrl ?? null, windowDays: l.windowDays ?? null, address: l.address ?? null,
})
export const destinationOf = (p: AppPlatform): DestinationRef => ({
  locationId: p.locationId, platform: p.platform, state: p.state, label: p.label,
  ...(p.url ? { url: p.url } : {}), ...(p.name ? { name: p.name } : {}), ...(p.icon ? { icon: p.icon } : {}),
})
export const socialOf = (s: AppSocial): SocialRef => ({ locationId: s.locationId, social: s.social, url: s.url, handle: s.handle ?? null, followers: s.followers ?? null })
/** A fiche the server has read (or a Google link pasted, with nothing read yet); null when the establishment has none. */
export const ficheOf = (f: AppFiche | null, l: ServerLocation): Fiche | null => !f || !f.url ? null : {
  url: f.url, name: f.name ?? l.name, address: f.address ?? '', rating: f.rating == null ? '' : f.rating.toFixed(1).replace('.', ','),
  hours: (f.hours ?? []).join(' · '), readAt: f.readAt ?? '',
  ...(f.ficheUrl ? { ficheUrl: f.ficheUrl } : f.placeId ? { ficheUrl: `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(f.placeId)}` } : {}),
}
/** D-44: the monthly cap lives on the roue's row, so a type carries none; its stock is what this month's caps leave. */
export const typeOf = (t: AppGiftType): GiftType => ({
  id: t.id, name: t.name, internal: t.internal ?? t.name, cost: t.costCents / 100, validityDays: t.validityDays ?? 60, cap: null,
  condition: t.condition ?? '', status: t.stock?.status ?? 'ok', stock: t.stock ? Math.max(0, t.stock.capThisMonth - t.stock.issuedThisMonth) : null,
  ...(t.icon ? { icon: t.icon } : {}), category: t.category, sameDay: !!t.sameDay, links: '',
})
export const roueOf = (r: AppRoue): Roue => ({
  id: r.id, name: r.name, scope: r.scope, locations: [...r.locations], reveal: { ...r.reveal },
  ...(r.texts ? { texts: { ...r.texts } } : {}), ...(r.design ? { design: r.design } : {}),
  rewards: r.rewards.map(x => ({ rewardId: x.rewardId, name: x.name, cost: x.costCents / 100, weight: x.weight, cap: x.cap,
    ...(x.excludedReason === 'stock' ? { excluded: 'stock épuisé ce mois' } : {}) })),
  usedBy: r.usedBy.map(u => u.name), ...(r.pending ? { pending: true } : {}),
})
export const askOf = (a: AppAsk): Ask => ({
  id: a.id, kind: a.kind, name: a.name, category: a.category, endMessage: a.endMessage ?? '', createdAt: a.createdAt,
  answers: a.answers, usedBy: a.usedBy.map(u => u.name),
  /* the gift an ask names is no field of the server's: the app's default for its kind (blankAsk) */
  gift: a.kind === 'retour' ? { mode: 'aucun' } : { mode: 'roue' },
  ...(a.kind === 'quiz' ? { questions: (a.questions ?? []).map(q => ({ id: q.id, text: q.text, choices: [...q.choices], correct: q.correct })) } : {}),
  ...(a.kind === 'question' ? { question: a.question ?? '', answerType: a.answerType ?? 'choix', choices: [...(a.choices ?? [])],
    freeLine: a.freeLine ?? a.answerType === 'libre' } : {}),
  ...(a.kind === 'retour' ? { prompt: a.prompt ?? '' } : {}),
})

/** lib-pack-19 · a saved preset as the server answers it: the app's own shape, field by field, nothing else kept. */
export const linkAskOf = (a: LinkAsk): LinkAsk => ({
  id: a.id, kind: a.kind, name: a.name, platforms: (a.platforms ?? []).map(p => ({ platform: p.platform, share: p.share })),
  socials: [...(a.socials ?? [])], createdAt: a.createdAt, usedBy: [...(a.usedBy ?? [])],
})
/** lib-pack-21 · an étiquette as GET /v1/app/tags answers it (02 §2.5): no position on the wire, the list's order is it;
    `counts` the server's (P3). The app's shape, field by field. */
export interface AppTag { id: string; name: string; createdAt: string; counts: { campaigns: number; supports: number } }
export const tagOf = (t: AppTag, position: number): Tag => ({
  id: t.id, name: t.name, position, createdAt: t.createdAt ?? '', counts: { campaigns: t.counts?.campaigns ?? 0, supports: t.counts?.supports ?? 0 },
})
/** lib-pack-23 · a catégorie / an emplacement as GET /v1/app/categories, /v1/app/places answer them (02 §2.6, 0039): the
    étiquettes' shape, one count each. The app's shape, field by field. */
export interface AppCategory { id: string; name: string; createdAt: string; counts: { campaigns: number } }
export interface AppPlace { id: string; name: string; createdAt: string; counts: { supports: number } }
export const categoryOf = (c: AppCategory, position: number): Category => ({ id: c.id, name: c.name, position, createdAt: c.createdAt ?? '', counts: { campaigns: c.counts?.campaigns ?? 0 } })
export const placeOf = (p: AppPlace, position: number): Place => ({ id: p.id, name: p.name, position, createdAt: p.createdAt ?? '', counts: { supports: p.counts?.supports ?? 0 } })
/** lib-pack-23 · a list an API without 0039 does not have yet: its 404, or an answer that is not the list (not JSON, no
    `items` array), reads as null (not there yet) and never stops the boot; any other refusal is thrown as it is. */
export const notYet = <T extends { items?: unknown },>(read: Promise<T>): Promise<T | null> => read.then(
  r => (r && Array.isArray(r.items) ? r : null),
  e => { if ((e instanceof ApiError && e.status === 404) || e instanceof SyntaxError) return null; throw e })
/** A member: who, where (none: the whole workspace), whether a code is set, the login's role, the server's 30 days. */
export const memberOf = (m: AppMember): Member => ({
  id: m.id, firstName: m.firstName, locationId: m.locationId ?? null, pinSet: !!m.pinSet, access: m.access ?? 'none',
  numbers: m.numbers ? { scans30d: m.numbers.scans30d, participations30d: m.numbers.participations30d, giftsValidated30d: m.numbers.giftsValidated30d } : null,
})

/** The sector the app knows, what the customer fills in (the e-mail always), and the look: the brand's logo travels in
    the app's design (`logoUrl`), as Identité visuelle keeps it, and the brand's colour (D-86, what the customer page wears)
    is its Marque colour (`a`). No look saved yet reads as the app's default. */
export const workspaceOf = (w: AppWorkspace): ServerWorkspace => {
  const b = w.brand
  return {
    sector: SECTEURS.some(x => x.id === w.sector) ? w.sector as SecteurId : null,
    identityFields: { ...IDENTITY_DEFAULT, ...(w.identityFields ?? {}) },
    wheelDesign: b && (b.wheelDesign || b.logoUrl || b.colour)
      ? { ...DEFAULT_DESIGN, ...(b.wheelDesign ?? {}), ...(b.colour ? { a: b.colour } : {}), logoUrl: b.logoUrl ?? null } : null,
  }
}

/* ---------- a campaign, back from the server's documents (05 §5.2–5.3) ---------- */
const AT_DAY = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/
/** The day the merchant picked, from the instant the document carries (lib/campaign-api startOf / endOf). */
const dayOf = (v: string | null | undefined, end: boolean): string | null => {
  if (v == null) return null
  const m = AT_DAY.exec(v)
  return m && m[2] === (end ? '23:59:59' : '00:00:00') ? m[1] : v
}
/** « 10 octobre », as the app's own publish writes the day. */
const frDay = (iso: string | null | undefined): string | null => {
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? null : new Date(t).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
}
/** One gift per version (I-4 D1): a demande carries none, so it reads as the app's default. */
const demandeOfDoc = (d: DocDemande): Demande => ({
  kind: d.kind, platforms: (d.platforms ?? []).map(p => ({ platform: p.platform, share: p.share })), askId: d.askId ?? null, gift: { mode: 'roue' },
  ...(d.social ? { social: d.social } : {}), ...(d.socials?.length ? { socials: [...d.socials] } : {}),
  ...(d.socialShares?.length ? { socialShares: [...d.socialShares] } : {}), /* lib-pack-22 · point 5 */
})
/** lib-pack-20 · a visit row read back (0035, per-visit paper § 2.1): its own gift, its own order — absent = the campaign's,
    as the step was saved. The ids are the server's (the mirror's roues and types carry them). */
const stepOfDoc = (st: DocStep): ParcoursStep => ({
  id: st.id, demande: demandeOfDoc(st.demande),
  ...(st.gift ? { gift: st.gift.mode === 'roue' ? { mode: 'roue', roueId: st.gift.roueId } : st.gift.mode === 'cadeau' ? { mode: 'cadeau', typeId: st.gift.typeId } : { mode: 'aucun' } } : {}),
  ...(st.flow ? { flow: st.flow, moment: st.moment ?? (st.flow === 'cadeau_apres_invitation' ? 'pendant' : 'immediat'), ...(st.countdown !== undefined ? { countdown: st.countdown } : {}) } : {}),
})
const shapeOfDoc = (d: CampaignDocument): Shape => ({
  type: d.shape.type === 'parcours' ? 'parcours' : 'simple', demande: demandeOfDoc(d.shape.demande),
  steps: d.shape.type === 'parcours' ? (d.shape.steps ?? []).map(stepOfDoc) : [],
  ...(d.shape.type === 'parcours' && d.shape.after ? { after: d.shape.after } : {}),
})
const roueIdOf = (d: CampaignDocument): string | null => (d.gift?.mode === 'roue' ? d.gift.roueId : null)
/** I-7 · the direct gift's type, when the version gives one that way */
const giftTypeIdOf = (d: CampaignDocument): string | null => (d.gift?.mode === 'cadeau' ? d.gift.typeId : null)
/** The campaign the builder and the screens read: its live version, the next one waiting in `pending`, its versions. A
    draft (version 0) is its pending document. A version the server keeps without a note reads as the app's own publish. */
export function campaignOfRead(r: CampaignRead, askName?: (id: string) => string | undefined): Campaign | null {
  const doc = r.live ?? r.pending
  if (!doc) return null
  const row: CampaignRow = r.campaign
  const next = r.live && r.pending ? r.pending : null
  const shape = shapeOfDoc(doc)
  const overrides = Object.entries(doc.overrides ?? {})
  return {
    id: row.id, name: r.live ? row.name : doc.name, status: row.status, version: row.version,
    locations: [...(row.locations.length ? row.locations : doc.locations)], goals: [...(doc.goals ?? [])],
    recipe: recipeFromShape(shape, roueIdOf(doc), doc.moment, askName, giftTypeIdOf(doc)),
    countdown: doc.countdown, earlyOpen: doc.earlyOpen, tone: doc.tone, tu: doc.tu, texts: { ...doc.texts },
    fallback: doc.fallback, startAt: dayOf(doc.startAt, false) ?? 'now', endAt: dayOf(doc.endAt, true), ageMin: doc.ageMin,
    acks: { ...doc.acks }, testedAt: row.testedAt, createdAt: '', publishedAt: frDay(row.publishedAt),
    createdVia: 'wizard', customised: false,
    ...(doc.theme ? { theme: doc.theme } : {}), ...(overrides.length ? { overrides: Object.fromEntries(overrides.map(([l, o]) => [l, { roueId: o.roueId }])) } : {}),
    shape, textLength: doc.textLength, stepReached: doc.stepReached, flow: doc.flow,
    ...(doc.look ? { look: { ...doc.look } } : {}), ...(doc.thanks ? { thanks: doc.thanks } : {}),
    ...(row.seq != null ? { seq: row.seq } : {}),
    ...(row.tags ? { tags: [...row.tags] } : {}), /* lib-pack-21 · row data (0036): never in a document */
    ...(row.categoryId !== undefined ? { categoryId: row.categoryId } : {}), /* lib-pack-23 · row data (0039): never in a document */
    versions: r.versions.slice().sort((a, b) => a.version - b.version)
      .map(v => ({ version: v.version, publishedAt: frDay(v.publishedAt) ?? '', note: v.note ?? (v.version === 1 ? 'première mise en ligne' : 'modifiée — nouvelle version') })),
    objective: doc.objective ? { kind: doc.objective.kind, n: doc.objective.n } : null,
    ...(next ? { pending: { shape: shapeOfDoc(next), texts: { ...next.texts }, textLength: next.textLength, name: next.name, roueId: roueIdOf(next), giftTypeId: giftTypeIdOf(next),
      stepReached: next.stepReached, moment: next.moment, flow: next.flow, countdown: next.countdown, earlyOpen: next.earlyOpen,
      ...(next.look ? { look: { ...next.look } } : {}), ...(next.thanks ? { thanks: next.thanks } : {}) } } : {}),
  }
}

/* ---------- the read ---------- */
/** Everything the getters read, from the server, in one go: the lists and the workspace, then each campaign in full and
    each fiche. Archived campaigns are not listed by the server (05 §5.4), so they are not read. The principale of each
    establishment is the rows' `principal` (I-1). Kept in lib/mirror and returned. */
export async function loadMirror(): Promise<Mirror> {
  /* lib-pack-18: who is signed in is read with them (01 §1.3) — lib/scope's role gates read the server's user, not a
     user this browser kept from the demo */
  const [locations, platforms, socials, types, roues, asks, rows, workspace, members, library, tags, categories, places] = await Promise.all([
    readMe().then(() => get<ServerLocation[]>('/app/locations')), get<AppPlatform[]>('/app/platforms'), get<AppSocial[]>('/app/socials'),
    get<AppGiftType[]>('/app/gift-types'), get<AppRoue[]>('/app/roues'), get<{ asks: AppAsk[] }>('/app/asks'), get<CampaignRow[]>('/app/campaigns'),
    get<AppWorkspace>('/app/workspace'), get<AppMember[]>('/app/members'),
    /* lib-pack-19 · the saved avis / social presets (04 §4.4): the app's LinkAsk on the wire, in the order the merchant keeps */
    get<{ items: LinkAsk[] }>('/app/library'),
    /* lib-pack-21 · the « Étiquettes » list (02 §2.5), with the server's counts */
    get<{ items: AppTag[] }>('/app/tags'),
    /* lib-pack-23 · « Catégories » and « Emplacements » (02 §2.6, 0039): an API without them answers 404, read as not there yet */
    notYet(get<{ items: AppCategory[] }>('/app/categories')), notYet(get<{ items: AppPlace[] }>('/app/places')),
  ])
  const locs = locations.map(locationOf)
  const askList = asks.asks.map(askOf)
  const askName = (id: string) => askList.find(a => a.id === id)?.name
  const [reads, fiches] = await Promise.all([
    Promise.all(rows.map(r => readCampaign(r.id))),
    Promise.all(locs.map(async l => [l.id, ficheOf(await get<AppFiche>(`/app/locations/${encodeURIComponent(l.id)}/fiche`), l)] as const)),
  ])
  setMirror({
    locations: locs, destinations: platforms.map(destinationOf), socials: socials.map(socialOf), workspace: workspaceOf(workspace),
    types: types.map(typeOf), roues: roues.map(roueOf), asks: askList,
    campaigns: reads.map(r => campaignOfRead(r, askName)).filter((c): c is Campaign => !!c),
    fiches: Object.fromEntries(fiches),
    principals: Object.fromEntries(reads.flatMap(r => (r.campaign.principal ?? []).map(l => [l, r.campaign.id] as const))),
    members: members.map(memberOf),
    library: library.items.map(linkAskOf),
    tags: tags.items.map(tagOf),
    /* each campaign's étiquettes as its row carries them; a support's are noted when its list is read (lib/campaign › mergeSupports) */
    itemTags: { campaigns: Object.fromEntries(reads.map(r => [r.campaign.id, [...(r.campaign.tags ?? [])]])), supports: {} },
    /* lib-pack-23 · each campaign's catégorie as its row carries it; a support's emplacement is noted when its list is read */
    categories: (categories?.items ?? []).map(categoryOf), places: (places?.items ?? []).map(placeOf), orgReady: !!categories && !!places,
    itemCategory: categories ? Object.fromEntries(reads.map(r => [r.campaign.id, r.campaign.categoryId ?? null])) : {}, itemPlace: {},
  })
  return mirror()
}

/** The app's root (App.tsx), around every route that reads these: with an API, the mirror is read once for the tab, and
    the routes render once it is there (the establishments seed ['locations'] too); the demo renders them at once. */
export function ServerMirror() {
  const qc = useQueryClient()
  const { pathname, search } = useLocation()
  const q = useQuery({
    queryKey: ['mirror'], enabled: apiWrites, staleTime: Infinity, gcTime: Infinity,
    queryFn: async () => { const m = await loadMirror(); qc.setQueryData(['locations'], m.locations); return true },
  })
  if (!apiWrites || q.data) return <Outlet />
  /* lib-pack-18 (WF-047): no session → the sign-in door, keeping the page wanted as ?suite=; on the doors themselves the
     line below says it instead (they sit outside this gate in App.tsx, so this is only the belt) */
  if (q.isError && q.error instanceof ApiError && q.error.status === 401 && !isAuthPath(pathname)) return <Navigate to={signInPath(pathname + search)} replace />
  if (q.isError) return (
    <div style={GATE}>
      <div className="empty" role="alert" data-testid="mirror-error">
        <p className="empty-title">{q.error instanceof ApiError && q.error.status === 401
          ? refusalLines(q.error)[0]
          : 'Le serveur ne répond pas : rien n’a pu être lu. Vérifiez la connexion, puis réessayez.'}</p>
        <div><Button size="sm" variant="primary" onClick={() => { void q.refetch() }}>Réessayer</Button></div>
      </div>
    </div>
  )
  return <div style={{ ...GATE, display: 'grid', gap: 10 }} aria-busy="true" data-testid="mirror-loading"><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
}
/** Both waits of the root sit alone on the page (no shell yet): one centred column. */
const GATE = { maxWidth: 560, margin: '0 auto', padding: '72px 24px' } as const

/* ---------- the drafts (« Enregistrer le brouillon », 05 §5.3) ---------- */
/** With an API, a draft is the server's. Kept in this tab at once (saveCampaign), then written there: created the first time
    (POST /v1/app/campaigns; its server id replaces the local one, and the local one keeps finding it in this tab), its
    pending document after (PATCH …/pending, `rev` read back first). One write at a time, in order (lib/mirror › queueWrite),
    each sending the copy as it is when its turn comes; `onId` re-keys what the caller keeps, `onRefused` says a refusal. The
    demo keeps the draft in this browser and calls nothing. It answers the draft's id once it is saved (null if refused). */
export function saveDraft(c: Campaign, opts: { onId?: (from: string, to: string) => void; onRefused?: (e: unknown) => void } = {}): Promise<string | null> {
  saveCampaign(c)
  if (!apiWrites) return Promise.resolve(c.id)
  return queueWrite(async () => {
    const cur = getCampaign(c.id)
    if (!cur) return null
    if (!isServerId(cur.id)) {
      /* lib-pack-21 · the étiquettes chosen on step 3 before the draft existed go with its creation (0036 P8) */
      /* lib-pack-23 · and its catégorie (0039: optional on the POST), only once the server has the lists */
      const made = await createCampaign(documentOf(cur), itemTagIds('campaign', cur.id), organisationReady() ? campaignCategoryId(cur.id) : null)
      rekeyCampaign(cur.id, made.campaign.id)
      opts.onId?.(cur.id, made.campaign.id)
      return made.campaign.id
    }
    const read = await readCampaign(cur.id)
    await savePending(cur.id, documentOf(cur), read.pendingRev)
    return cur.id
  }).catch(e => { opts.onRefused?.(e); return null })
}
/** « Annuler les modifications »: with an API, DELETE …/pending on the server's campaign (after the writes still on their
    way), then this tab's copy drops its next version; the demo drops it here. */
export async function discardPending(id: string): Promise<void> {
  if (apiWrites) {
    await queueWrite(async () => {
      const cur = getCampaign(id)
      if (cur && isServerId(cur.id)) await call<void>('DELETE', `/app/campaigns/${encodeURIComponent(cur.id)}/pending`)
    })
  }
  dropPending(id)
}
